/**
 * Calculate distance between two geographic coordinates using Haversine formula
 * @param {number} lat1 - Latitude of first point
 * @param {number} lon1 - Longitude of first point
 * @param {number} lat2 - Latitude of second point
 * @param {number} lon2 - Longitude of second point
 * @returns {number} - Distance in meters
 */
export function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000; // Earth's radius in meters

  // Convert degrees to radians
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  // Haversine formula
  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // Distance in meters
}

/**
 * Convert meters to feet
 * @param {number} meters
 * @returns {number} - Distance in feet
 */
export function metersToFeet(meters) {
  return meters * 3.28084;
}

/**
 * Format distance for display
 * @param {number} meters
 * @returns {string} - Formatted distance string
 */
export function formatDistance(meters) {
  const feet = metersToFeet(meters);

  if (meters < 10) {
    return `${Math.round(meters)}m (${Math.round(feet)}ft) - Perfect!`;
  } else if (meters < 100) {
    return `${Math.round(meters)}m (${Math.round(feet)}ft)`;
  } else {
    return `${Math.round(meters)}m (${Math.round(feet)}ft)`;
  }
}

/**
 * Calculate score based on distance
 * Closer guesses = higher scores
 * @param {number} distanceInMeters
 * @returns {number} - Score (0-1000)
 */
export function calculateScore(distanceInMeters) {
  const MAX_SCORE = 1000;
  const DISTANCE_PENALTY = 10; // points per meter

  const score = Math.max(0, MAX_SCORE - Math.floor(distanceInMeters * DISTANCE_PENALTY));
  return score;
}

/**
 * Get performance rating based on score
 * @param {number} score
 * @returns {string} - Rating (Excellent, Great, Good, Fair, Poor)
 */
export function getScoreRating(score) {
  if (score >= 900) return 'Excellent!';
  if (score >= 700) return 'Great!';
  if (score >= 500) return 'Good';
  if (score >= 300) return 'Fair';
  return 'Keep Practicing!';
}
